module Shared.Admin.AddUser
open Shared
open Shared.Admin.ConfigTypes
open System
open Shared.Zxcvbn
open Illy.SafeStack

type InitData = 
    {
        Applications: ManageApplications.InitApplicationDTO list
        Licenses: LicenseCounts
        EditFieldsConfig: AdditionalFieldsConfig
        PasswordConfig: PasswordConfig
    }

type SaveDTO = 
    {
        Username: string option
        EditFields: EditUser.DTO
        Password: string option
        ConfirmPassword: string option
        Applications: ManageApplications.SaveApplicationDTO list
    }

module ValidationTypes =
    type Username = 
        {
            UserId: int 
            Username: string
        }

module Validations = 
    open Illy.SafeStack.Validation
    open Illy.SafeStack.ROP
    open Illy.SafeStack.CompositionalValidation
    open Illy.SafeStack.SharedValidations
    open ValidationTypes
    open Shared.Validations.PasswordStrength.Validations
    open Shared.PasswordReset.ResetPassword.Validations

    type UsernameValidations = 
        | StringValidations of StringValidations 
        | MustBeUnique 

    module UsernameValidations = 
        let toString = 
            function
            | UsernameValidations.StringValidations v -> v.toString()
            | UsernameValidations.MustBeUnique -> "This username is already taken - please choose another."

    type GeneralValidations = 
        | OverLicensesLimit of licensesLimit: int

    module GeneralValidations = 
        let toString = 
            function
            | GeneralValidations.OverLicensesLimit licensesLimit -> sprintf "You are already using all of your %d licenses - please contact the ILLY team to purchase additional licenses; alternatively, you can disable users to free up their licenses." licensesLimit

    type AddUserValidations = 
        | UsernameValidations of UsernameValidations
        | EditFields of EditUser.Validations.EditUserValidations
        | PasswordValidations of PasswordStrengthValidations
        | ConfirmPasswordValidations of ConfirmPasswordValidations
        | GeneralValidations of GeneralValidations

    module AddUserValidations = 
        let toString = 
            function 
            | UsernameValidations v -> UsernameValidations.toString v
            | EditFields v -> EditUser.Validations.EditUserValidations.toString v
            | PasswordValidations v -> PasswordStrengthValidations.toString v
            | ConfirmPasswordValidations v -> ConfirmPasswordValidations.toString v
            | GeneralValidations v -> GeneralValidations.toString v
    
    let validateUsername (usernames: Username list) username = 
        validate {
            return! (validateString 250 true >-> UsernameValidations.StringValidations) username
            return! !!(error UsernameValidations.MustBeUnique) 
                >>= passIfNone username 
                >>= conditional pass (fun (_, str) -> usernames |> List.exists (fun x -> x.Username = str) |> not)
                |> firstResult
        }

    let validateGeneral (licenses: LicenseCounts) = 
        validate {
            if licenses.Used + 1 > licenses.Total then 
                return! (error (GeneralValidations.OverLicensesLimit licenses.Total))
        }

    let validateDTO scorePassword passwordConfig licenses usernames (dto: SaveDTO) = 
        validate {
            return! (validateUsername usernames >-> AddUserValidations.UsernameValidations) dto.Username
            return! (EditUser.Validations.validateDTO >-> AddUserValidations.EditFields) dto.EditFields
            return! (validatePasswordStrength passwordConfig scorePassword >-> AddUserValidations.PasswordValidations) dto.Password
            return! (validateConfirmPassword passwordConfig.PasswordStrength dto.Password >-> AddUserValidations.ConfirmPasswordValidations) dto.ConfirmPassword
            return! (validateGeneral >-> AddUserValidations.GeneralValidations) licenses
        }

type FailureReason = 
    | FailedValidations of Validation.ValidationResult<Validations.AddUserValidations, Validation.NoWarning>

type ComposedFailureReason = 
    | CreateUser of FailureReason 
    | Apps of ManageApplications.FailureReason
    